import { useEffect, useRef } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import { ConversationThinkingIndicator } from './ConversationThinkingIndicator'
import messages from './messages'
import { renderConversationItem } from './renderConversationItem'
import { ConversationStreamListStyled } from './styles/ConversationStreamStyled'
import {
  type ConversationCatalogItemComponent,
  type ConversationChoiceOption,
  type ConversationTurnItem,
} from '../../../../value-objects/ConversationTurnItem'

export interface IConversationStreamProps {
  items: ConversationTurnItem[]
  isStreaming: boolean
  thinkingLabel?: Nullable<string>
  onChoiceSelect: (option: ConversationChoiceOption) => void
  onSendMessage: (message: string) => void
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
}

export const ConversationStream = ({
  items,
  isStreaming,
  thinkingLabel = null,
  onChoiceSelect,
  onSendMessage,
  onCatalogItemSelect,
}: IConversationStreamProps) => {
  const intl = useProcurifyIntl()
  const feedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    feedRef.current?.scrollIntoView?.({ block: 'end' })
  }, [items, isStreaming])

  if (items.length === 0 && !isStreaming) {
    return null
  }

  return (
    <ConversationStreamListStyled
      ref={feedRef}
      role='log'
      aria-live='polite'
      aria-label={intl.formatMessage(messages.feedAriaLabel)}
    >
      {items.map((item) => (
        <div key={item.id}>
          {renderConversationItem(item, {
            isStreaming,
            onChoiceSelect,
            onSendMessage,
            onCatalogItemSelect,
          })}
        </div>
      ))}
      {isStreaming && <ConversationThinkingIndicator label={thinkingLabel} />}
    </ConversationStreamListStyled>
  )
}
